import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import { actionTemplates } from '../../../../../api/coreData'

const useStyles = makeStyles(() => ({
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--text-color-subtle)',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '12px'
  },
  toggleCard: {
    padding: '14px 16px',
    border: '1px solid var(--color-neutral-150)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&:not(:last-child)': {
      marginBottom: '10px'
    }
  },
  toggleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  toggleLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-color-primary)'
  },
  toggleDesc: {
    fontSize: '11px',
    color: 'var(--text-color-subtle)'
  },
  footer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid var(--color-neutral-150)'
  }
}))

function SettingsTab ({ template, onSave, onToast }) {
  const classes = useStyles()
  const [processing, setProcessing] = useState(false)
  const { mutateAsync: modifyActionTemplate } = actionTemplates.useModifyActionTemplate()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: template?.name || '',
      description: template?.description || '',
      enabled: !!template?.enabled,
      visibleToAi: !!template?.visibleToAi
    }
  })

  const handleSave = useCallback(async (formData) => {
    try {
      setProcessing(true)

      await modifyActionTemplate({
        actionTemplateId: template.actionTemplateId,
        name: formData.name,
        description: formData.description || null,
        enabled: formData.enabled,
        visibleToAi: formData.visibleToAi
      })

      form.reset(formData)
      onSave()
      if (onToast) onToast('Settings saved successfully')
    } catch (err) {
      if (onToast) onToast(err.message || 'Failed to save settings', 'error')
    } finally {
      setProcessing(false)
    }
  }, [modifyActionTemplate, template, onSave, onToast, form])

  return (
    <div>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <div className={classes.sectionLabel}>General</div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SydLabel label='Code' description='Immutable identifier for this action template'>
                <SydInput value={template?.code || ''} disabled />
              </SydLabel>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='name'
                control={form.control}
                rules={{
                  required: { value: true, message: 'Required.' },
                  maxLength: { value: 128, message: 'Max 128 Characters.' }
                }}
                render={(f) => (
                  <SydLabel label='Name' error={hookFormErrorAdapter(f, f.fieldState)} required>
                    <SydInput {...f.field} />
                  </SydLabel>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name='description'
                control={form.control}
                rules={{
                  maxLength: { value: 500, message: 'Max 500 Characters.' }
                }}
                render={(f) => (
                  <SydLabel label='Description' error={hookFormErrorAdapter(f, f.fieldState)}>
                    <SydInput {...f.field} multiline rows={3} />
                  </SydLabel>
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <div className={classes.sectionLabel}>Flags</div>
          <Controller
            name='enabled'
            control={form.control}
            render={({ field }) => (
              <div className={classes.toggleCard}>
                <div className={classes.toggleInfo}>
                  <span className={classes.toggleLabel}>Enabled</span>
                  <span className={classes.toggleDesc}>Whether this template can be used</span>
                </div>
                <SydSwitch value={field.value} onChange={(val) => field.onChange(val)} />
              </div>
            )}
          />
          <Controller
            name='visibleToAi'
            control={form.control}
            render={({ field }) => (
              <div className={classes.toggleCard}>
                <div className={classes.toggleInfo}>
                  <span className={classes.toggleLabel}>Visible to AI</span>
                  <span className={classes.toggleDesc}>AI agents can discover this template</span>
                </div>
                <SydSwitch value={field.value} onChange={(val) => field.onChange(val)} />
              </div>
            )}
          />
        </Grid>
      </Grid>
      <div className={classes.footer}>
        <SydButton
          variant='primary'
          onClick={form.handleSubmit(handleSave)}
          processing={processing}
          disabled={!form.formState.isDirty}
        >
          Save Settings
        </SydButton>
      </div>
    </div>
  )
}

SettingsTab.propTypes = {
  template: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onToast: PropTypes.func
}

export default SettingsTab
